// Global
import React from 'react';
import dynamic from 'next/dynamic';
import { SpeedInsights } from '@vercel/speed-insights/next';
import {
  Placeholder,
  LayoutServiceData,
  HTMLLink,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { Environment, PageController, WidgetsProvider } from '@sitecore-search/react';
import { GoogleTagManager } from '@next/third-parties/google';

// Lib
import { ThemeContext, ThemeName } from 'lib/context/ThemeContext';
import useExperienceEditor from 'lib/use-experience-editor';

// Local
import HrefLang from 'helpers/HeadSection/HrefLang';
import PageHeadSection from 'helpers/HeadSection/PageHeadSection';
import SkipToMain from 'helpers/SkipToMain/SkipToMain';
import jssConfig from 'src/temp/config';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Scripts from './Scripts';

const BazaarVoiceScript = dynamic(() => import('helpers/BazaarVoice/BazaarVoiceScript'), {
  loading: () => <p>Loading...</p>,
});
const PowerReviewsScript = dynamic(() => import('helpers/PowerReviews/PowerReviewsScript'), {
  loading: () => <p>Loading...</p>,
});
interface LayoutProps {
  layoutData: LayoutServiceData;
  headLinks: HTMLLink[] | null;
}

function splitLanguageAndCountry(inputValue: string | undefined) {
  // Default country code
  const defaultCountry = 'us';

  // Ensure inputValue is not undefined
  if (!inputValue) {
    inputValue = ''; // Set a default value or handle it according to your needs
  }

  // Split the input string using '-'
  const parts = inputValue.split('-');

  // Extract language and country
  const language = parts[0];
  const country = parts.length > 1 ? parts[1] : defaultCountry;

  return {
    language: language?.toLowerCase(),
    country: country?.toLowerCase(),
  };
}

const Layout = ({ layoutData, headLinks }: LayoutProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const enableBazaarSetting = sitecoreContext?.bazaarVoice?.enableBazaarVoice as
    | boolean
    | undefined;

  const enablePowerReviewSetting = sitecoreContext?.powerReviews?.enablePowerReviews as
    | boolean
    | undefined;
  const { context, route } = layoutData.sitecore;
  const { gtmDetails, language, pageEditing } = context;
  const isExperienceEditor = useExperienceEditor();

  let appName = (context.site?.name as ThemeName) ?? 'Vanilla';

  if (isExperienceEditor && context?.sitefallBackTheme?.fallbackAppName) {
    appName = context?.sitefallBackTheme?.fallbackAppName as ThemeName;
  } else if (isExperienceEditor && context?.sitefallBackTheme?.fallbackAppNameLocalised) {
    appName = context?.sitefallBackTheme?.fallbackAppNameLocalised as ThemeName;
  } else {
    appName = !jssConfig.fallbackAppName
      ? (context.site?.name as ThemeName) ?? 'Vanilla'
      : (jssConfig.fallbackAppName as ThemeName);
  }

  const secondaryThemes = process.env.NEXT_PUBLIC_SECONDARY_THEME;
  if (secondaryThemes) {
    const secondaryTheme: [string, string][] = JSON.parse(secondaryThemes);

    secondaryTheme.forEach(([site, locale]) => {
      if (site && locale === language) {
        appName = site as ThemeName;
      }
    });
  }

  const localValues = splitLanguageAndCountry(language);
  const pageHeader = route?.displayName;

  PageController.getContext().setLocaleLanguage(localValues.language);
  PageController.getContext().setLocaleCountry(localValues.country);

  const mainClassPageEditing = pageEditing ? 'editing-mode' : 'prod-mode';

  const headerRef = useRef<HTMLDivElement>(null);

  const router = useRouter();

  const enableRTL = context?.enableRTL as boolean | undefined;
  const direction = enableRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.classList.remove('visually-none');
    }
  }, [router]);

  let gtm_id = gtmDetails?.gtmId;
  if (jssConfig.gtmByPath !== '') {
    const itemPath = context?.itemPath?.toString() || '';
    const entries = jssConfig.gtmByPath
      .split('&')
      .map((entry) => entry.split('='))
      .filter(([key, value]) => key && value);
    for (const [key, value] of entries) {
      if (itemPath.startsWith(key)) {
        gtm_id = value;
        break;
      }
    }
  }

  return (
    <>
      <ThemeContext.Provider value={appName}>
        <Scripts />
        <PageHeadSection headLinks={headLinks} language={language} route={route} />
        <HrefLang />
        {isExperienceEditor || !gtm_id?.length ? null : <GoogleTagManager gtmId={gtm_id} />}
        <WidgetsProvider
          apiKey={jssConfig.sitecoreSearchApiKey}
          customerKey={jssConfig.sitecoreSearchCustomerKey}
          env={jssConfig.sitecoreSearchEnv as Environment}
          publicSuffix={true}
        >
          <div className={`${mainClassPageEditing} ${appName}`} dir={direction}>
            <SkipToMain />
            <span className="sr-only">{pageHeader}</span>
            <div id="topbar">
              {route && <Placeholder name="dart-topbanner" rendering={route} />}
            </div>
            <header className="w-full z-[99] top-0 sticky" ref={headerRef} id="header">
              <div id="topbanner">
                {route && <Placeholder name="dart-header" rendering={route} />}
              </div>
            </header>
            <main>
              <div id="content">
                {route && <Placeholder name="dart-breadcrumb" rendering={route} />}
                {route && <Placeholder name="dart-hero" rendering={route} />}
                <div id="main" tabIndex={-1}>
                  {route && <Placeholder name="dart-main" rendering={route} />}
                </div>
              </div>
            </main>
            <footer>
              <div id="footer">{route && <Placeholder name="dart-footer" rendering={route} />}</div>
            </footer>
          </div>
        </WidgetsProvider>
      </ThemeContext.Provider>
      {enableBazaarSetting ? (
        <>
          <BazaarVoiceScript />
          <PowerReviewsScript />
        </>
      ) : (
        enablePowerReviewSetting && <PowerReviewsScript />
      )}
      <SpeedInsights />
    </>
  );
};

export default Layout;
